import { useCallback, useEffect, useState } from 'react'

import { fetchBlobUrl } from '../../lib/api'
import { errorText } from '../../lib/offsite'
// หมายเหตุ: ไม่ import API_BASE แล้ว — รูปโหลดผ่าน fetchBlobUrl (เติม base ให้เอง)
import { useSession } from '../../lib/SessionContext'
import { canEdit } from '../../lib/nav'

/** รูปย่อ: โหลดผ่าน fetch (ส่ง cookie) แล้วทำเป็น blob — <img> ข้าม origin ไม่ส่ง cookie (ดู api.fetchBlobUrl) */
function BlobImg({ path, className, alt }: { path: string; className?: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let alive = true
    let made: string | null = null
    fetchBlobUrl(path)
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u)
          return
        }
        made = u
        setUrl(u)
      })
      .catch(() => {})
    return () => {
      alive = false
      if (made) URL.revokeObjectURL(made)
    }
  }, [path])
  if (!url) return <span className={className} aria-label={alt} />
  return <img className={className} src={url} alt={alt} />
}

/* ============================================================================
   สไลด์ banner หน้าแรก — จัดการโดย admin · `/admin/slides`
   API: backend/src/routes/adminSlides.js · lib/slides.js · ตาราง company_slide (018)

   รูปเก็บลง DB (VARBINARY) เหมือนไฟล์ข่าว · ที่เพิ่ม/เปิดไว้จะไปโชว์หน้าแรกฝั่งพนักงาน
   (Home.tsx → SlideShow) · ลากลำดับด้วยปุ่มขึ้น/ลง
   ============================================================================ */

type Slide = {
  id: number
  title: string | null
  linkUrl: string | null
  fileName: string | null
  fileSize: number | null
  sortOrder: number
  isActive: boolean
}

type Draft = {
  title: string
  linkUrl: string
  isActive: boolean
  // รูป: fileData = base64 data URL ของรูปใหม่ (เพิ่มใหม่ต้องมี · แก้ไขว่างได้ = คงรูปเดิม)
  fileData: string | null
  fileName: string | null
}

const emptyDraft = (): Draft => ({
  title: '',
  linkUrl: '',
  isActive: true,
  fileData: null,
  fileName: null,
})

const MAX_FILE_MB = 10

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'))
    r.readAsDataURL(file)
  })
}

export default function Slides() {
  const { state, call } = useSession()
  const editable = state.status === 'signed-in' && canEdit(state.session)

  const [items, setItems] = useState<Slide[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // แผงแก้ไข: null = ปิด · {id:null} = เพิ่มใหม่ · {id:n} = แก้
  const [editing, setEditing] = useState<{ id: number | null } | null>(null)
  const [draft, setDraft] = useState<Draft>(emptyDraft())
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const d = await call<{ items: Slide[] }>('/api/admin/slides')
      setItems(d.items)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [call])

  useEffect(() => {
    void load()
  }, [load])

  function openAdd() {
    setDraft(emptyDraft())
    setEditing({ id: null })
  }
  function openEdit(r: Slide) {
    setDraft({
      title: r.title ?? '',
      linkUrl: r.linkUrl ?? '',
      isActive: r.isActive,
      fileData: null,
      fileName: r.fileName,
    })
    setEditing({ id: r.id })
  }

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setError(`รูปใหญ่เกิน ${MAX_FILE_MB} MB`)
      e.target.value = ''
      return
    }
    try {
      const dataUrl = await readFileAsDataUrl(file)
      setDraft((d) => ({ ...d, fileData: dataUrl, fileName: file.name }))
    } catch (err) {
      setError(errorText(err))
    }
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const body = { ...draft }
      if (editing?.id == null) {
        await call('/api/admin/slides', { method: 'POST', body: JSON.stringify(body) })
      } else {
        await call(`/api/admin/slides/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) })
      }
      setEditing(null)
      await load()
    } catch (err) {
      setError(errorText(err))
    } finally {
      setSaving(false)
    }
  }

  async function remove(r: Slide) {
    if (!confirm(`ลบสไลด์ "${r.title || r.fileName || r.id}" ?`)) return
    setError(null)
    try {
      await call(`/api/admin/slides/${r.id}`, { method: 'DELETE' })
      await load()
    } catch (err) {
      setError(errorText(err))
    }
  }

  /** ย้ายลำดับขึ้น/ลง แล้วส่งลำดับใหม่ทั้งชุดให้ backend (reorder) */
  async function move(i: number, dir: -1 | 1) {
    const j = i + dir
    if (j < 0 || j >= items.length) return
    const next = items.slice()
    ;[next[i], next[j]] = [next[j], next[i]]
    setItems(next) // อัปเดตหน้าจอทันที แล้วค่อยยืนยันกับ server
    setError(null)
    try {
      await call('/api/admin/slides/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ids: next.map((s) => s.id) }),
      })
      await load()
    } catch (err) {
      setError(errorText(err))
      await load() // ล้มเหลว → ดึงลำดับจริงกลับมา
    }
  }

  return (
    <>
      <h1>สไลด์หน้าแรก</h1>

      <section className="card">
        <div className="section-head">
          <p className="meta">
            รูป banner ที่โชว์บนสุดของหน้าแรกพนักงาน · ปิดไว้ = ยังไม่แสดง · ลำดับบน = โชว์ก่อน
          </p>
          {editable && (
            <button type="button" onClick={openAdd} disabled={loading}>
              เพิ่มสไลด์
            </button>
          )}
        </div>
        {error && <p className="error">{error}</p>}

        {loading ? (
          <p className="meta">กำลังโหลด…</p>
        ) : items.length === 0 ? (
          <p className="meta">ยังไม่มีสไลด์</p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>ตัวอย่าง</th>
                  <th>คำบรรยาย</th>
                  <th>ลิงก์</th>
                  <th>สถานะ</th>
                  {editable && <th>ลำดับ</th>}
                  {editable && <th></th>}
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={r.id} className={r.isActive ? undefined : 'meta'}>
                    <td>
                      <BlobImg
                        className="slide-thumb"
                        path={`/api/admin/slides/${r.id}/image`}
                        alt={r.title ?? ''}
                      />
                    </td>
                    <td>{r.title || <span className="meta">—</span>}</td>
                    <td className="cell-clip">
                      {r.linkUrl ? (
                        <a href={r.linkUrl} target="_blank" rel="noopener noreferrer">
                          {r.linkUrl}
                        </a>
                      ) : (
                        <span className="meta">—</span>
                      )}
                    </td>
                    <td className="nowrap">
                      {r.isActive ? (
                        <span className="chip chip--ok">แสดง</span>
                      ) : (
                        <span className="chip chip--off">ปิด</span>
                      )}
                    </td>
                    {editable && (
                      <td className="row-actions nowrap">
                        <button
                          type="button"
                          className="ghost"
                          disabled={i === 0}
                          onClick={() => void move(i, -1)}
                          aria-label="เลื่อนขึ้น"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          disabled={i === items.length - 1}
                          onClick={() => void move(i, 1)}
                          aria-label="เลื่อนลง"
                        >
                          ↓
                        </button>
                      </td>
                    )}
                    {editable && (
                      <td className="row-actions nowrap">
                        <button type="button" className="ghost" onClick={() => openEdit(r)}>
                          แก้
                        </button>
                        <button type="button" className="ghost danger" onClick={() => void remove(r)}>
                          ลบ
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && (
        <section className="card form-card">
          <h2>{editing.id == null ? 'เพิ่มสไลด์' : 'แก้ไขสไลด์'}</h2>
          <div className="field-grid">
            <div className="kv field-grid--full">
              <span className="kv-label">
                รูปภาพ (png/jpg/webp/gif ไม่เกิน {MAX_FILE_MB}MB)
                {editing.id == null && ' — บังคับ'}
              </span>
              <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif" onChange={onPickFile} />
              {draft.fileData ? (
                <span className="meta">จะอัป: {draft.fileName}</span>
              ) : draft.fileName ? (
                <span className="meta">รูปเดิม: {draft.fileName} (ไม่เลือกใหม่ = คงรูปเดิม)</span>
              ) : null}
            </div>

            <label className="kv field-grid--full">
              <span className="kv-label">คำบรรยาย (ว่างได้)</span>
              <input
                type="text"
                value={draft.title}
                maxLength={200}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </label>

            <label className="kv field-grid--full">
              <span className="kv-label">ลิงก์เมื่อกดสไลด์ (ว่าง = ไม่ลิงก์)</span>
              <input
                type="url"
                value={draft.linkUrl}
                maxLength={1000}
                placeholder="https://…"
                onChange={(e) => setDraft({ ...draft, linkUrl: e.target.value })}
              />
            </label>

            <label className="kv">
              <span className="kv-label">แสดงหน้าแรก</span>
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
            </label>
          </div>

          <div className="row-actions">
            <button
              type="button"
              onClick={save}
              // เพิ่มใหม่ต้องมีรูป · แก้ไขไม่บังคับ (คงรูปเดิม)
              disabled={saving || (editing.id == null && !draft.fileData)}
            >
              {saving ? 'กำลังบันทึก…' : 'บันทึก'}
            </button>
            <button type="button" className="ghost" onClick={() => setEditing(null)} disabled={saving}>
              ยกเลิก
            </button>
          </div>
        </section>
      )}
    </>
  )
}
